// Quel club sortir du sac.

import { temperedRate } from './clubFocus.ts';

/** Un club du sac, vu par l'écran de parcours. */
export interface ClubEnJeu {
    id: string;
    label: string;
    category: string;
    /** Distance déclarée à l'équipement, en mètres. `null` si non renseignée. */
    referenceDistanceM: number | null;
    /** Coups frappés au practice, et coups centrés parmi eux. */
    shots: number;
    center: number;
    /** Distance moyenne TRAVAILLÉE au practice sur les coups centrés.
     *
     *  Ce n'est pas la portée du club : c'est la cible que le joueur s'est
     *  donnée et qu'il a atteinte. Elle sert à expliquer un choix — « tu le
     *  travailles à 70 m » — jamais à le faire. */
    practiceDistanceM?: number | null;
    /** Les deux journées de practice sur lesquelles `shots` et `center` sont
     *  comptés, de la plus récente à la plus ancienne, au format `YYYY-MM-DD`.
     *
     *  La fenêtre est propre à CHAQUE club — on ne sort pas tout le sac à
     *  chaque sortie. Elle sert à le dire à l'écran : un club rare est jugé
     *  sur des séances vieilles de cinq semaines, et le taire serait mentir. */
    windowDays?: string[];
}

export interface ClubClasse extends ClubEnJeu {
    /** Taux de réussite brut, `null` sous le premier coup. */
    rate: number | null;
    /** Écart à la distance visée, `null` si le club n'a pas de distance. */
    ecartM: number | null;
}

/* ── Pourquoi la distance vient du sac et le taux du practice ────────────────
 *
 * Deux sources, et une seule sait faire chaque chose.
 *
 * `reference_distance_m` est déclarée à l'équipement : c'est la portée que le
 * joueur attribue à son club. Elle couvre tout le sac.
 *
 * La distance relevée au practice ne peut PAS servir ici : c'est la cible de
 * l'exercice, pas la portée de la balle. Le même sac porte un Fer 7 déclaré à
 * 120 m et relevé à 175 m, un Fer 5 déclaré à 140 m et relevé à 74 m — ce ne
 * sont pas des erreurs de saisie, ce sont deux grandeurs différentes.
 *
 * Le practice sait en revanche dire ce qu'on RÉUSSIT, et c'est ce qu'on lui
 * demande : départager deux clubs également proches de la cible.
 */

/** Le putter ne se PROPOSE jamais : il n'a rien à faire sur un départ, et
 *  aucune distance de référence à comparer. Il reste choisissable à la main. */
const EXCLUS = new Set(['putter']);

export function clubsJouables(clubs: ClubEnJeu[]): ClubEnJeu[] {
    return clubs.filter((c) => !EXCLUS.has(c.category));
}

/** Le taux moyen du joueur, repère du lissage. */
function moyenne(clubs: ClubEnJeu[]): number {
    const shots = clubs.reduce((s, c) => s + c.shots, 0);
    const center = clubs.reduce((s, c) => s + c.center, 0);
    return shots > 0 ? (center * 100) / shots : 0;
}

/** La liste du sélecteur : chaque club avec sa distance et son taux.
 *
 *  Ordonnée par distance décroissante, comme le sac lui-même. Le joueur y
 *  choisit d'après SON estimation de la distance restante — l'application ne
 *  la connaît pas hors du départ, et prétendre le contraire l'induirait en
 *  erreur.
 */
export function listeDesClubs(clubs: ClubEnJeu[], cibleM: number | null = null): ClubClasse[] {
    // Le putter est dans la LISTE mais jamais dans la suggestion : on ne le
    // propose pas, mais un joueur qui putte doit pouvoir le désigner — sinon
    // ses putts sortent du parcours sans club.
    return clubs
        .map((c) => ({
            ...c,
            rate: c.shots > 0 ? (c.center * 100) / c.shots : null,
            ecartM: c.referenceDistanceM != null && cibleM != null
                ? Math.abs(c.referenceDistanceM - cibleM)
                : null,
        }))
        .sort((a, b) => (b.referenceDistanceM ?? -1) - (a.referenceDistanceM ?? -1));
}

/** Ce que l'écran a besoin de savoir sur la suggestion. */
export interface Suggestion {
    club: ClubEnJeu;
    /** Vrai quand un club couvre vraiment la distance : c'est ce qui autorise
     *  la pastille. Faux quand on ne fait que s'en approcher. */
    sur: boolean;
    /** Ce qu'il resterait à couvrir après ce coup, si le club n'atteint pas. */
    resteM: number | null;
}

/* Atteindre n'est pas symétrique.
 *
 * Un joueur peut toujours RETENIR un club — swing plus court, balle plus
 * basse — mais jamais l'allonger. Un club trop long reste donc une réponse ;
 * un club trop court n'en est pas une, il laisse la balle devant le green.
 *
 * D'où deux bornes différentes : cinq mètres de manque, l'ordre de grandeur de
 * l'estimation d'un joueur, et vingt mètres d'excès, au-delà desquels on ne
 * propose plus un club mais un compromis. */
const MANQUE_TOLERE_M = 5;
const EXCES_TOLERE_M = 20;

/** Deux clubs sont concurrents en deçà de cet écart : à cinq mètres près, le
 *  choix ne se joue plus sur la distance mais sur ce qu'on réussit. */
const ECART_CONCURRENT_M = 8;

/** Le club à sortir pour couvrir `cibleM`.
 *
 *  1. on garde le plus proche de la cible ;
 *  2. tous ceux qui sont à moins de huit mètres de lui sont ses concurrents ;
 *  3. le meilleur taux l'emporte — lissé par le volume, parce que 50 % sur
 *     deux balles n'est pas 40 % sur cent vingt ;
 *  4. si rien ne couvre la distance, on propose quand même le plus proche,
 *     SANS le présenter comme une réponse : `sur` est faux, l'écran n'affiche
 *     alors aucune pastille et dit ce qu'il reste.
 *
 *  Rend `null` seulement quand le sac est vide de clubs mesurables : il n'y a
 *  alors rien à proposer, et inventer serait pire que se taire.
 */
export function clubPour(clubs: ClubEnJeu[], cibleM: number): Suggestion | null {
    const candidats = clubsJouables(clubs).filter((c) => c.referenceDistanceM != null);
    if (candidats.length === 0) return null;

    const ecart = (c: ClubEnJeu): number => Math.abs(c.referenceDistanceM! - cibleM);
    const meilleurEcart = Math.min(...candidats.map(ecart));
    const concurrents = candidats.filter((c) => ecart(c) - meilleurEcart <= ECART_CONCURRENT_M);

    /* LE TAUX NE PEUT PAS FAIRE PERDRE LA DISTANCE.
     *
     * Deux concurrents, l'un qui couvre et l'autre non : c'est le premier qui
     * gagne, quel que soit le taux. Le départage au taux n'a de sens qu'entre
     * clubs qui répondent tous à la question ; l'ouvrir à un club trop court,
     * c'est échanger une réponse sûre contre un coup laissé devant le green.
     *
     * Le défaut ne se voyait guère sur les distances rondes d'une carte de
     * parcours. Il saute aux yeux dès qu'on saisit une mesure au télémètre :
     * à 137 m, le fer 5 à 140 couvre et le fer 6 à 130 manque de sept mètres
     * — or le fer 6 centre mieux, et il l'emportait. L'écran proposait alors
     * un club en avouant qu'il ne suffirait pas, quand un autre suffisait.
     *
     * Si AUCUN ne couvre, on garde tout le monde : le plus proche sera proposé
     * sans être présenté comme une réponse, ce qui est le cas honnête. */
    const couvre = (c: ClubEnJeu): boolean => {
        const porte = c.referenceDistanceM!;
        return porte + MANQUE_TOLERE_M >= cibleM && porte - cibleM <= EXCES_TOLERE_M;
    };
    const couvrants = concurrents.filter(couvre);
    const finalistes = couvrants.length > 0 ? couvrants : concurrents;

    // Le taux tranche entre finalistes, mais la distance garde le dernier
    // mot a taux egal : sans ce second critere, un sac sans aucune statistique
    // les egalise tous et le premier de la liste l'emporte — on proposerait un
    // club a huit metres quand un autre tombe a deux.
    const moy = moyenne(clubs);
    const club = [...finalistes].sort((a, b) => {
        const dr = temperedRate(b.center, b.shots, moy) - temperedRate(a.center, a.shots, moy);
        return Math.abs(dr) > 1e-9 ? dr : ecart(a) - ecart(b);
    })[0]!;

    const porte = club.referenceDistanceM!;
    const sur = porte + MANQUE_TOLERE_M >= cibleM && porte - cibleM <= EXCES_TOLERE_M;
    const reste = porte < cibleM ? Math.round(cibleM - porte) : null;
    return { club, sur, resteM: sur ? null : reste };
}
